"use client";

import { useRouter } from "next/navigation";
import type { BaseListItem, SortOrder } from "@/types";
import { DataTable, EmptyState } from "@/components/shared";
import { RankCards } from "./RankCards";
import { createRankColumns } from "../config";

type RankItem = BaseListItem & { rank: number };

function extractAppId(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number.parseInt(value, 10);
    return Number.isFinite(n) ? n : null;
  }
  if (value && typeof value === "object" && "appid" in value) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return extractAppId((value as any).appid);
  }
  return null;
}

interface RankListProps<T extends RankItem> {
  items: T[];
  sortBy: SortOrder;
  onSortChange: (sort: SortOrder) => void;
  primaryColor: string;
  emptyMessage?: string;
}

export function RankList<T extends RankItem>({
  items,
  sortBy,
  onSortChange,
  primaryColor,
  emptyMessage = "No items found.",
}: RankListProps<T>) {
  const router = useRouter();

  // Whole row is a real <a href> (stretched link in DataTable) so the game opens
  // with middle-click / cmd-click / right-click → "Open in new tab" anywhere.
  const rowHref = (item: T) => {
    const appid = extractAppId(item.appid);
    return appid == null ? null : `/app/${appid}`;
  };

  const handleSortChange = (sortKey: string) => {
    if (sortKey === "rank") {
      const newSort: SortOrder = sortBy === "asc" ? "desc" : "asc";
      onSortChange(newSort);
    }
  };

  if (items.length === 0) {
    return <EmptyState message={emptyMessage} />;
  }

  const columns = createRankColumns<T>(primaryColor);

  return (
    <div className="bg-[#16202d] rounded-lg border border-[#2a475e] overflow-hidden">
      <DataTable
        data={items}
        columns={columns}
        getRowHref={rowHref}
        getRowAriaLabel={(item) => item.name}
        getRowKey={(item) =>
          extractAppId(item.appid) ?? `fallback-${item.rank}-${item.name}`
        }
        currentSortKey={sortBy ? "rank" : null}
        sortOrder={sortBy}
        onSortChange={handleSortChange}
      />
      <RankCards
        items={items}
        onCardClick={(appid) => router.push(`/app/${appid}`)}
        primaryColor={primaryColor}
      />
    </div>
  );
}

